import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import Footer from "../Footer";
import { useNavigate } from "react-router-dom";
import {
  minLengthRegex,
  capitalLetterRegex,
  numberRegex,
} from "../../common/commonFun";
import { getSMSText } from "../../common/utilities";
import { ABSOLUTE_URL } from "../../common/constants";
import { yupResolver } from "@hookform/resolvers/yup";
import { CoPayPatientMyDetailsMobileValSchema } from "../ValidationForm/validationSchema";
import { CoPayPatientMyDetailsBankDetailsValSchema } from "../ValidationForm/validationSchema";
import { CoPayPatientMyDetailsPasswordValSchema } from "../ValidationForm/validationSchema";
import { toast } from "react-toastify";
import Loader from "./../../common/Loader";
import smsTemplateService from "../../services/smsTemplateService";
import SupportPatientHeader from "../SupportPatientHeader";
import SupportPatientService from "../../services/SupportPatientService";
import SupportMessageModal from "../modal/SupportPatient/SupportMessageModal";
import useModal from "../modal/useModal";

const SupportPatientMyDetails = () => {
  const form = useRef();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const supportPatientId = localStorage.getItem("supportPatientId");

  const {
    handleSubmit: handleSubmitMobile,
    register: registerMobile,
    setValue: setValueMobile,
    formState: { errors: errorsMobile },
    reset: resetMobile,
  } = useForm({
    resolver: yupResolver(CoPayPatientMyDetailsMobileValSchema),
    defaultValues: { supportPatientId: supportPatientId },
  });

  const {
    handleSubmit: handleSubmitBankDetails,
    register: registerBankDetails,
    setValue: setValueBankDetails,
    formState: { errors: errorsBankDetails },
    reset: resetBankDetails,
  } = useForm({
    resolver: yupResolver(CoPayPatientMyDetailsBankDetailsValSchema),
    defaultValues: { supportPatientId: supportPatientId },
  });

  const {
    handleSubmit: handleSubmitPassword,
    register: registerPassword,
    watch: watchPassword,
    formState: { errors: errorsPassword },
    reset: resetPassword,
  } = useForm({
    resolver: yupResolver(CoPayPatientMyDetailsPasswordValSchema),
    defaultValues: { supportPatientId: supportPatientId },
  });

  const password = watchPassword("password");
  const confirmPassword = watchPassword("confirmPassword");

  // States for Password Validation
  let [passwordValidation, setPasswordValidation] = useState({
    eightCharacters: false,
    capitalLetter: false,
    oneNumber: false,
    passwordMatch: false,
  });
  const { isShowing: isShowMessage, toggle: toggleShowMessage } = useModal();
  const [message, setMessage] = useState('');
  const [displayBsb, setDisplayBsb] = useState('');
  const [displayAccountNumber, setDisplayAccountNumber] = useState('');
  const [displayMobile, setDisplayMobile] = useState('');

  const handleChangeMobile = (event) => {
    const input = event.target.value.replace(/[^0-9]/g, '');
    let formattedInput = input.slice(0, 10);

    if (formattedInput.length === 10) {
      const firstPart = formattedInput.slice(0, 7);
      const secondPart = formattedInput.slice(7);
      setValueMobile("mobile", `${firstPart}${secondPart}`);
      setDisplayMobile(`xxxxxxx${secondPart}`); // Mask the first 3 digits
    } else {
      setDisplayMobile(formattedInput);
      setValueMobile("mobile", formattedInput);
    }

  };
  const handleChangeBsb = (event) => {
    const input = event.target.value.replace(/[^0-9]/g, '');
    let formattedInput = input.slice(0, 6);

    if (formattedInput.length === 6) {
      const firstPart = formattedInput.slice(0, 3);
      const secondPart = formattedInput.slice(3);
      setValueBankDetails("bsb", `${firstPart}${secondPart}`);
      setDisplayBsb(`xxx-${secondPart}`); // Mask the first 3 digits
    } else {
      setDisplayBsb(formattedInput);
      setValueBankDetails("bsb", formattedInput);
    }

  };
  const handleChangeAccount = (event) => {
    const input = event.target.value.replace(/[^0-9]/g, '');
    let formattedInput = input.slice(0, 6);

    if (formattedInput.length === 6) {
      const firstPart = formattedInput.slice(0, 3);
      const secondPart = formattedInput.slice(3);
      setValueBankDetails("accNumber", `${firstPart}${secondPart}`);
      setDisplayAccountNumber(`xxx-${secondPart}`); // Mask the first 3 digits
    } else {
      setDisplayAccountNumber(formattedInput);
      setValueBankDetails("accNumber", formattedInput);
    }
  };
  useEffect(() => {
    if (password || confirmPassword) {
      validatePassword(password, confirmPassword);
    }
  }, [password, confirmPassword]);

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        const bankDetailsResponse = await SupportPatientService.getSupportPatientBankDetails(supportPatientId);
        const detailsResponse = await SupportPatientService.getSupportPatientDetails(supportPatientId);

        localStorage.setItem("supportPatientMobile", detailsResponse.data.data.mobile);

        const { patientBankDetailsData } = bankDetailsResponse.data;
        resetMobile();
        resetBankDetails();
        resetPassword();
        setValueMobile("mobile", localStorage.getItem("supportPatientMobile"));
        setValueBankDetails("accName", patientBankDetailsData.accName);
        setValueBankDetails("bsb", patientBankDetailsData.bsb);
        setValueBankDetails("accNumber", patientBankDetailsData.accNumber);
        setDisplayBsb(`xxx-${patientBankDetailsData.bsb.toString().slice(-3)}`);
        setDisplayAccountNumber(`xxx-${patientBankDetailsData.accNumber.toString().slice(-3)}`);
        setDisplayMobile(`xxxxxxx${localStorage.getItem("supportPatientMobile").slice(-3)}`);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        navigate("/");
      }
    };

    fetchData();
  }, [supportPatientId, navigate]);

  const validatePassword = (password, confirmPassword) => {
    if (!password && !confirmPassword) {
      setPasswordValidation(false);
      return;
    }

    setPasswordValidation({
      ...passwordValidation,
      eightCharacters: minLengthRegex.test(password) ? true : false,
      capitalLetter: capitalLetterRegex.test(password) ? true : false,
      oneNumber: numberRegex.test(password) ? true : false,
      passwordMatch: password === confirmPassword ? true : false,
    });
  };

  const handleUpdateMobile = (data) => {
    setLoading(true);
    SupportPatientService.requestToUpdateSupportPatientMobile(supportPatientId, data).then(
      async (response) => {
        setLoading(false);
        setMessage(response.data.message);
        toggleShowMessage();
        // toast.success(response.data.message);
        try {
          let link_confirm =
            ABSOLUTE_URL +
            "verify-support-patient-mobile-number/" +
            response.data.data.token;
          let link_block =
            ABSOLUTE_URL +
            "not-verify-support-patient-mobile-number/" +
            response.data.data.token;
          let smsText = await getSMSText("sms_update_support_patient_mobile", [
            { patient_first_name: response.data.data.firstname },
            { link_confirm: link_confirm },
            { link_block: link_block },
          ]);
          // console.log(link_confirm,"link_confirm mobile")
          if (smsText !== null && smsText !== undefined) {
            // smsTemplateService.sendSMS({to:"+918511367064", msg:smsText,userType:"support-patient",userId:supportPatientId});
            smsTemplateService.sendSMS({to:response.data.data.newMobile, msg:smsText,userType:"support-patient",userId:supportPatientId});
          }
        } catch (error) {
          console.error("Error while fetching SMS text:", error);
          toast.error("Error while fetching SMS text:", error);
        }
      },
      (error) => {
        const resMessage =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          error.message ||
          error.toString();

        setLoading(false);

        toast.error(resMessage);
      }
    );
  };

  const handleUpdateBankDetails = (data) => {
    setLoading(true);

    SupportPatientService.requestToUpdateSupportPatientBankDetails(supportPatientId, data).then(
      async (response) => {
        setLoading(false);
        setMessage(response.data.message);
        toggleShowMessage();
        // toast.success(response.data.message);

        try {
          let link_confirm =
            ABSOLUTE_URL +
            "verify-support-patient-bank-details/" +
            response.data.data.token;
          let link_block =
            ABSOLUTE_URL +
            "not-verify-support-patient-bank-details/" +
            response.data.data.token;
          let smsText = await getSMSText("sms_update_support_patient_bank", [
            { patient_first_name: response.data.data.firstname },
            { link_confirm: link_confirm },
            { link_block: link_block },
          ]);
          // console.log(link_confirm,"link_confirm bank")
          if (smsText !== null && smsText !== undefined) {
            // smsTemplateService.sendSMS({to:"+918511367064", msg:smsText,userType:"support-patient",userId:supportPatientId});
            smsTemplateService.sendSMS({to:response.data.data.mobile, msg:smsText,userType:"support-patient",userId:supportPatientId});
          }
        } catch (error) {
          console.error("Error while fetching SMS text:", error);
          toast.error("Error while fetching SMS text:", error);
        }
      },
      (error) => {
        const resMessage =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          error.message ||
          error.toString();

        setLoading(false);
        toast.error(resMessage);
      }
    );
  };

  const handleUpdatePassword = (data) => {
    if (
      passwordValidation.eightCharacters === true &&
      passwordValidation.capitalLetter === true &&
      passwordValidation.oneNumber === true &&
      passwordValidation.passwordMatch === true
    ) {
      setLoading(true);
      SupportPatientService.requestToUpdateSupportPatientPassword(supportPatientId, data).then(
        async (response) => {
          setLoading(false);

          setMessage(response.data.message);
          toggleShowMessage();
          // toast.success(response.data.message);
          resetPassword();

          setPasswordValidation({
            ...passwordValidation,
            eightCharacters: false,
            capitalLetter: false,
            oneNumber: false,
            passwordMatch: false,
          });

          try {
            let link_confirm =
              ABSOLUTE_URL +
              "verify-support-patient-password/" +
              response.data.data.token;
            let link_block =
              ABSOLUTE_URL +
              "not-verify-support-patient-password/" +
              response.data.data.token;
            let smsText = await getSMSText("sms_update_support_patient_password", [
              { patient_first_name: response.data.data.firstname },
              { link_confirm: link_confirm },
              { link_block: link_block },
            ]);

            if (smsText !== null && smsText !== undefined) {
              // smsTemplateService.sendSMS({to:"+918511367064", msg:smsText,userType:"support-patient",userId:supportPatientId});
              smsTemplateService.sendSMS({to:response.data.data.mobile, msg:smsText,userType:"support-patient",userId:supportPatientId});
            }
          } catch (error) {
            console.error("Error while fetching SMS text:", error);
            toast.error("Error while fetching SMS text:", error);
          }
        },
        (error) => {
          const resMessage =
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            error.message ||
            error.toString();

          setLoading(false);

          toast.error(resMessage);
          resetPassword();
        }
      );
    }
  };

  return (
    <>
      <SupportMessageModal isShowing={isShowMessage} hide={toggleShowMessage} message={message}/>
      <SupportPatientHeader />
      {loading && <Loader />}
      <main className="inner-page">
        <section className="container">
          <div className="row">
            <div className="col-lg-10">
              <h1 className="text-uppercase">My Details</h1>
              <p className="fs-18 text-dark-gray lh-sm">
                Please check your details and update anything that has changed.
                Please have your phone nearby as we will be sending you an SMS
                to authenticate this information when you click CONFIRM.
              </p>
              <div className="spacer-1"></div>
            </div>
          </div>
          <div className="row gx-3">
            <div className="col-lg-10">
              <form
                autoComplete="off"
                className="custom-form"
                onSubmit={handleSubmitMobile(handleUpdateMobile)}
                ref={form}
              >
                <div className="row">
                  <div className="col-md-9 d-flex flex-column flex-md-row align-items-md-center mb-3">
                    <label htmlFor="" className="form-label me-3 mb-md-0">
                      Mobile:
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Mobile Number"
                      name="mobile"
                      value={displayMobile}
                      maxLength={10}
                      onChange={handleChangeMobile}
                      // {...registerMobile("mobile")}
                    />
                  </div>
                  <div className="col-md-3">
                    <button className="btn btn-blue btn-default btn-block">
                    CONFIRM
                    </button>
                  </div>
                  {errorsMobile.mobile && (
                    <span>{errorsMobile.mobile.message}</span>
                  )}
                </div>
              </form>
              <form
                autoComplete="off"
                ref={form}
                className="custom-form"
                onSubmit={handleSubmitBankDetails(handleUpdateBankDetails)}
              >
                <div className="spacer-3"></div>
                <div className="row">
                  <div className="col-md-12 mb-2">
                    <h4 className="fs-22 text-uppercase text-dark-gray mb-2">
                      Current Bank Details
                    </h4>
                    <p className="fs-18 text-dark-gray lh-sm">
                      We pay your co-pay directly into your nominated bank
                      account. <br />
                      <br />
                      Please check the details and update anything that has
                      changed. Please have your phone nearby as we will be
                      sending you an SMS to authenticate this information when
                      you click CONFIRM.
                    </p>
                  </div>
                  <div className="col-md-8 mb-2">
                    <div className="row">
                      <div className="col-sm-12 mb-2">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Account Name"
                          name="accName"
                          
                          {...registerBankDetails("accName")}
                        />
                        {errorsBankDetails.accName && (
                          <p>{errorsBankDetails.accName.message}</p>
                        )}
                      </div>
                      <div className="col-sm-12 mb-2">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="BSB"
                          name="bsb"
                          value={displayBsb}
                          maxLength={7}
                          onChange={handleChangeBsb}
                          
                          // {...registerBankDetails("bsb")}
                        />
                        {errorsBankDetails.bsb && (
                          <p>{errorsBankDetails.bsb.message}</p>
                        )}
                      </div>
                      <div className="col-sm-12 mb-2">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Account Number"
                          name="accNumber"
                          value={displayAccountNumber}
                          maxLength={7}
                          onChange={handleChangeAccount}
                          // {...registerBankDetails("accNumber")}
                        />
                        {errorsBankDetails.accNumber && (
                          <p>{errorsBankDetails.accNumber.message}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <button className="btn btn-blue btn-default btn-block">
                      CONFIRM
                    </button>
                  </div>
                </div>
              </form>
              <form
                autoComplete="off"
                ref={form}
                className="custom-form"
                onSubmit={handleSubmitPassword(handleUpdatePassword)}
              >
                <div className="spacer-2"></div>
                <div className="row gx-2">
                  <div className="col-sm-12">
                    <h4 className="fs-18 fw-bold text-dark-gray mb-2">
                      Please set a password
                    </h4>
                  </div>
                  <div className="col-md-5 mb-2 mb-md-0">
                    <input
                      type="password"
                      className="form-control"
                      name="password"
                      placeholder="Password"
                      {...registerPassword("password")}
                    />
                    {errorsPassword.password && (
                      <p>{errorsPassword.password.message}</p>
                    )}
                  </div>
                  <div className="col-md-5 mb-3 mb-md-0">
                    <input
                      type="password"
                      className="form-control"
                      name="confirmPassword"
                      placeholder="Confirm Password"
                      {...registerPassword("confirmPassword")}
                    />
                    {errorsPassword.confirmPassword && (
                      <p>{errorsPassword.confirmPassword.message}</p>
                    )}
                  </div>
                  <div className="col-md-2">
                    <button className="btn btn-blue btn-default btn-block">
                    CONFIRM
                    </button>
                  </div>
                </div>
                <div className="spacer-2"></div>
                <div className="row">
                  <div className="col-sm-12">
                    <p className="mb-1 fs-18 text-dark-gray">
                      Please choose a secure password
                    </p>
                    <ul className="list-unstyled password-instructions text-dark-gray mb-0">
                      <li
                        className={
                          passwordValidation.eightCharacters ? "true" : "false"
                        }
                      >
                        At least 8 characters
                      </li>
                      <li
                        className={
                          passwordValidation.capitalLetter ? "true" : "false"
                        }
                      >
                        One capital letter
                      </li>
                      <li
                        className={
                          passwordValidation.oneNumber ? "true" : "false"
                        }
                      >
                        One number
                      </li>
                      <li
                        className={
                          passwordValidation.passwordMatch ? "true" : "false"
                        }
                      >
                        Password match
                      </li>
                    </ul>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default SupportPatientMyDetails;
